// Reine Hilfsfunktionen für Aufgaben (Liste, Board, „Meine Aufgaben“) – ohne React, damit sie sich testen lassen.
import type { Abschnitt, Aufgabe, AufgabeBezugTyp, Ich, MeineAufgabe, Prioritaet } from './api';

/** Anzeige für Aufgaben ohne verantwortliche Person (Filter, Board-Spalte). */
export const OHNE_VERANTWORTLICH = 'Niemand verantwortlich';
export const PRIO_NAME: Record<Prioritaet, string> = { niedrig: 'Niedrig', mittel: 'Mittel', hoch: 'Hoch' };
export const BEZUG_NAME: Record<AufgabeBezugTyp, string> = { prozess: 'Prozess', schritt: 'Schritt', entscheidung: 'Entscheidung' };

/** Heutiges Datum (lokal) als JJJJ-MM-TT. */
export function heuteIso(jetzt = new Date()): string {
  const z = (n: number) => String(n).padStart(2, '0');
  return `${jetzt.getFullYear()}-${z(jetzt.getMonth() + 1)}-${z(jetzt.getDate())}`;
}

/** Tage zwischen zwei JJJJ-MM-TT (b − a). */
export function tageZwischen(a: string, b: string): number {
  const t = (s: string) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
  return Math.round((t(b) - t(a)) / 86_400_000);
}

export type Faelligkeit = 'ueberfaellig' | 'heute' | 'demnaechst' | 'spaeter' | 'ohne';
export const FAELLIG_TITEL: Record<Faelligkeit, string> = {
  ueberfaellig: 'Überfällig',
  heute: 'Heute',
  demnaechst: 'Demnächst (7 Tage)',
  spaeter: 'Später',
  ohne: 'Ohne Datum',
};

/** Einordnung nach Fälligkeit; „demnächst“ = in den nächsten 7 Tagen. */
export function faelligkeit(faellig: string | null | undefined, heute: string): Faelligkeit {
  if (!faellig) return 'ohne';
  const d = tageZwischen(heute, faellig.slice(0, 10));
  if (d < 0) return 'ueberfaellig';
  if (d === 0) return 'heute';
  if (d <= 7) return 'demnaechst';
  return 'spaeter';
}

/** Datum kurz auf Deutsch: „heute“, „morgen“, „gestern“, sonst TT.MM. (mit Jahr, wenn nicht dieses Jahr). */
export function datumKurz(faellig: string, heute: string): string {
  const d = tageZwischen(heute, faellig);
  if (d === 0) return 'heute';
  if (d === 1) return 'morgen';
  if (d === -1) return 'gestern';
  const [j, m, t] = faellig.slice(0, 10).split('-');
  return `${t}.${m}.` + (j === heute.slice(0, 4) ? '' : j);
}

/** „Meine Aufgaben“ gruppiert nach Fälligkeit (Reihenfolge: überfällig, heute, demnächst, später, ohne Datum). */
export function meineGruppieren<T extends Pick<MeineAufgabe, 'faellig' | 'erledigt'>>(liste: T[], heute: string) {
  const gruppen: { art: Faelligkeit; titel: string; aufgaben: T[] }[] = (Object.keys(FAELLIG_TITEL) as Faelligkeit[])
    .map((art) => ({ art, titel: FAELLIG_TITEL[art], aufgaben: [] as T[] }));
  for (const a of liste) gruppen.find((g) => g.art === faelligkeit(a.faellig, heute))!.aufgaben.push(a);
  for (const g of gruppen) g.aufgaben.sort((x, y) => (x.faellig ?? '').localeCompare(y.faellig ?? ''));
  return gruppen.filter((g) => g.aufgaben.length > 0);
}

/** Reihenfolge-Wert zwischen zwei Nachbarn (Ziehen im Board); ohne Nachbarn 1. */
export function reihenfolgeZwischen(vorher?: number | null, nachher?: number | null): number {
  if (vorher == null && nachher == null) return 1;
  if (vorher == null) return nachher! - 1;
  if (nachher == null) return vorher + 1;
  return (vorher + nachher) / 2;
}

export type FaelligFilter = '' | 'ueberfaellig' | 'heute' | 'woche' | 'ohne';
const FAELLIG_WERTE = ['ueberfaellig', 'heute', 'woche', 'ohne'];

export interface Filter {
  /**
   * '' = alle, 'ich' = mir zugewiesen oder ich bin beteiligt, 'niemand' = ohne Verantwortliche(n),
   * sonst E-Mail/Name (verantwortlich oder beteiligt)
   */
  zustaendig: string;
  /** '' = alle */
  faellig: FaelligFilter;
  erledigt: 'offen' | 'erledigt' | 'alle';
  /** '' = alle, 'ohne' = ohne Bezug, sonst Bezugstyp */
  bezug: '' | 'ohne' | AufgabeBezugTyp;
  /** Freitext: jedes Wort muss in Titel oder Beschreibung vorkommen (ohne Groß-/Kleinschreibung) */
  text: string;
  /** nur von mir gemerkte Aufgaben (Stern) */
  gemerkt: boolean;
}
export const FILTER_LEER: Filter = { zustaendig: '', faellig: '', erledigt: 'offen', bezug: '', text: '', gemerkt: false };

/** Ist irgendein Filter vom Standard abweichend gesetzt? */
export const filterAktiv = (f: Filter) => filterSuche(f) !== '';

/** Sichtbare ID einer Aufgabe bzw. eines Meilensteins: mindestens dreistellig („003“). */
export const nrText = (nr: number | null | undefined) => (nr == null ? '' : String(nr).padStart(3, '0'));

/** Freitextsuche: alle Wörter von `text` kommen in ID, Titel oder Beschreibung vor; leerer Text passt immer. */
export function textPasst(a: Pick<Aufgabe, 'titel' | 'beschreibung'> & { nr?: number }, text: string): boolean {
  const woerter = text.toLowerCase().split(/\s+/).filter(Boolean);
  if (woerter.length === 0) return true;
  const heu = (nrText(a.nr) + '\n' + a.titel + '\n' + (a.beschreibung ?? '')).toLowerCase();
  return woerter.every((w) => heu.includes(w));
}

/** Fälligkeit gegen den Filter „Alle Termine / Überfällig / Heute / 7 Tage / Ohne Datum“ prüfen. */
export function faelligPasst(faellig: string | null | undefined, f: FaelligFilter, heute: string): boolean {
  if (!f) return true;
  const k = faelligkeit(faellig, heute);
  return f === 'woche' ? k === 'ueberfaellig' || k === 'heute' || k === 'demnaechst' : k === f;
}

/**
 * Filter aus der Adresse, z. B. Sprung aus dem Cockpit oder geteilter Link:
 * ?faellig=ueberfaellig, ?erledigt=erledigt, ?zustaendig=ich, ?bezug=schritt, ?q=Rechnung, ?gemerkt=1.
 */
export function filterAusSuche(suche: string): Filter {
  const q = new URLSearchParams(suche);
  const faellig = q.get('faellig'), erledigt = q.get('erledigt'), zustaendig = q.get('zustaendig'), bezug = q.get('bezug'), text = q.get('q');
  const gemerkt = q.get('gemerkt') === '1';
  if (!faellig && !erledigt && !zustaendig && !bezug && !text && !gemerkt) return FILTER_LEER;
  return {
    ...FILTER_LEER,
    ...(faellig && FAELLIG_WERTE.includes(faellig) ? { faellig: faellig as FaelligFilter } : {}),
    ...(erledigt && ['offen', 'erledigt', 'alle'].includes(erledigt) ? { erledigt: erledigt as Filter['erledigt'] } : {}),
    ...(zustaendig ? { zustaendig } : {}),
    ...(bezug && ['ohne', 'prozess', 'schritt', 'entscheidung'].includes(bezug) ? { bezug: bezug as Filter['bezug'] } : {}),
    ...(text ? { text } : {}),
    ...(gemerkt ? { gemerkt } : {}),
  };
}

/** Filter als Abfrageteil der Adresse (ohne „?“); Standardwerte fallen weg, damit die Adresse kurz bleibt. */
export function filterSuche(f: Filter): string {
  const q = new URLSearchParams();
  if (f.zustaendig) q.set('zustaendig', f.zustaendig);
  if (f.faellig) q.set('faellig', f.faellig);
  if (f.erledigt !== 'offen') q.set('erledigt', f.erledigt);
  if (f.bezug) q.set('bezug', f.bezug);
  if (f.text.trim()) q.set('q', f.text);
  if (f.gemerkt) q.set('gemerkt', '1');
  return q.toString();
}

/** Ist die Person (E-Mail oder freier Name) an der Aufgabe beteiligt – als Verantwortliche(r) oder als weitere Beteiligte? */
export function istBeteiligt(a: Pick<Aufgabe, 'zustaendig' | 'beteiligte'>, person: string): boolean {
  const p = person.toLowerCase();
  if (!p) return false;
  return (a.zustaendig ?? '').toLowerCase() === p || (a.beteiligte ?? []).some((b) => b.toLowerCase() === p);
}

export function passt(a: Aufgabe, f: Filter, ich: string, heute: string): boolean {
  if (f.erledigt === 'offen' && a.erledigt) return false;
  if (f.erledigt === 'erledigt' && !a.erledigt) return false;
  if (f.zustaendig === 'ich' && !istBeteiligt(a, ich)) return false;
  if (f.zustaendig === 'niemand' && a.zustaendig) return false;
  if (f.zustaendig && f.zustaendig !== 'ich' && f.zustaendig !== 'niemand' && !istBeteiligt(a, f.zustaendig)) return false;
  if (!faelligPasst(a.faellig, f.faellig, heute)) return false;
  if (f.bezug === 'ohne' && a.bezugTyp) return false;
  if (f.bezug && f.bezug !== 'ohne' && a.bezugTyp !== f.bezug) return false;
  if (!textPasst(a, f.text)) return false;
  if (f.gemerkt && !a.gemerkt) return false;
  return true;
}

/**
 * Sichtbare Aufgaben einer Projektansicht (Liste, Board, Gantt): Unteraufgaben kommen immer mit (ihre Anzeige regelt die
 * Zeile), Hauptaufgaben, wenn sie zum Filter passen, gerade bearbeitet wurden („behalten“) oder – bei Freitext bzw. „Gemerkte“ –
 * eine ihrer Unteraufgaben (beliebiger Ebene) zum Filter passt.
 */
export function sichtbareAufgaben(aufgaben: Aufgabe[], f: Filter, ich: string, heute: string, behalten: ReadonlySet<string> = new Set()): Aufgabe[] {
  const kinder = new Map<string, Aufgabe[]>();
  for (const a of aufgaben) if (a.elternId) kinder.set(a.elternId, [...(kinder.get(a.elternId) ?? []), a]);
  const trefferUnten = (id: string, tiefe = 0): boolean =>
    tiefe < 20 && (kinder.get(id) ?? []).some((u) => passt(u, f, ich, heute) || trefferUnten(u.id, tiefe + 1));
  return aufgaben.filter((a) => a.elternId || behalten.has(a.id) || passt(a, f, ich, heute) || ((!!f.text.trim() || f.gemerkt) && trefferUnten(a.id)));
}

// ---------- „Meine Aufgaben“ auf der Startseite ----------

export interface MeinFilter {
  /** Freitext in Titel/Beschreibung */
  text: string;
  /** '' = alle Projekte, sonst Projekt-ID */
  projekt: string;
  faellig: FaelligFilter;
  /** erledigte mit anzeigen */
  erledigte: boolean;
  /** nur Aufgaben, für die ich verantwortlich bin (ohne die, an denen ich nur beteiligt bin) */
  nurZustaendig: boolean;
  /** nur von mir gemerkte Aufgaben (Stern) */
  nurGemerkt: boolean;
}
export const MEIN_FILTER_LEER: MeinFilter = { text: '', projekt: '', faellig: '', erledigte: false, nurZustaendig: false, nurGemerkt: false };

/** „Meine Aufgaben“-Filter aus der Adresse der Startseite: ?q=…&projekt=…&faellig=…&erledigte=1&nurzustaendig=1&gemerkt=1. */
export function meinFilterAusSuche(suche: string): MeinFilter {
  const q = new URLSearchParams(suche);
  const faellig = q.get('faellig') ?? '';
  return {
    text: q.get('q') ?? '',
    projekt: q.get('projekt') ?? '',
    faellig: FAELLIG_WERTE.includes(faellig) ? (faellig as FaelligFilter) : '',
    erledigte: q.get('erledigte') === '1',
    nurZustaendig: q.get('nurzustaendig') === '1',
    nurGemerkt: q.get('gemerkt') === '1',
  };
}

/** Setzt die „Meine Aufgaben“-Filter in eine vorhandene Abfrage (andere Parameter bleiben); liefert sie ohne „?“. */
export function meinFilterSuche(f: MeinFilter, vorher = ''): string {
  const q = new URLSearchParams(vorher);
  const setze = (k: string, v: string) => (v ? q.set(k, v) : q.delete(k));
  setze('q', f.text.trim() ? f.text : '');
  setze('projekt', f.projekt);
  setze('faellig', f.faellig);
  setze('erledigte', f.erledigte ? '1' : '');
  setze('nurzustaendig', f.nurZustaendig ? '1' : '');
  setze('gemerkt', f.nurGemerkt ? '1' : '');
  return q.toString();
}

export const meinFilterAktiv = (f: MeinFilter) => meinFilterSuche(f) !== '';

/**
 * Passt eine meiner Aufgaben zum Filter? „behalten“ = gerade abgehakte bzw. nicht mehr gemerkte, die stehen bleiben;
 * `ich` = eigene E-Mail (für „nur verantwortlich“).
 */
export function meinePasst(a: MeineAufgabe, f: MeinFilter, heute: string, behalten: ReadonlySet<string> = new Set(), ich = ''): boolean {
  if (a.erledigt && !f.erledigte && !behalten.has(a.id)) return false;
  if (f.nurZustaendig && (a.zustaendig ?? '').toLowerCase() !== ich.toLowerCase()) return false;
  if (f.nurGemerkt && !a.gemerkt && !behalten.has(a.id)) return false;
  if (f.projekt && a.projektId !== f.projekt) return false;
  if (!faelligPasst(a.faellig, f.faellig, heute)) return false;
  return textPasst(a, f.text);
}

/** Projekte zur Auswahl im Filter „Meine Aufgaben“: je Projekt einmal, sortiert nach Kunde und Titel. */
export function meineProjekte(liste: MeineAufgabe[]): { id: string; label: string }[] {
  const m = new Map<string, string>();
  for (const a of liste) if (!m.has(a.projektId)) m.set(a.projektId, `${a.kundeName} · ${a.projektTitel}`);
  return [...m].map(([id, label]) => ({ id, label })).sort((x, y) => x.label.localeCompare(y.label, 'de'));
}

/**
 * Darf ich in „Meine Aufgaben“ eine Aufgabe dieses Projekts abhaken? Ab mitwirkend laut Projektliste (ich.kunden),
 * Superadmins immer; archivierte Projekte sind nur lesbar. Unbekanntes Projekt: nur Superadmins.
 */
export function meineDarfSchreiben(ich: Pick<Ich, 'superadmin' | 'kunden'> | null | undefined, projektId: string): boolean {
  if (!ich) return false;
  const p = ich.kunden.flatMap((k) => k.projekte).find((x) => x.id === projektId);
  if (p?.archiviertAm) return false;
  return ich.superadmin || (!!p && p.rolle !== 'lesend');
}

const nachReihenfolge = (x: Aufgabe, y: Aufgabe) => x.reihenfolge - y.reihenfolge || x.erstelltAm.localeCompare(y.erstelltAm) || x.id.localeCompare(y.id);

/**
 * Hauptaufgaben (ohne elternId) je Abschnitt, sortiert; Abschnitte in ihrer Reihenfolge.
 * Aufgaben ohne bzw. mit unbekanntem Abschnitt landen in der Gruppe id=null („Ohne Abschnitt“), die nur
 * erscheint, wenn sie Aufgaben hat oder es gar keine Abschnitte gibt.
 */
export function nachAbschnitten(aufgaben: Aufgabe[], abschnitte: Abschnitt[]) {
  const sortiert = [...abschnitte].sort((a, b) => a.reihenfolge - b.reihenfolge || a.name.localeCompare(b.name));
  const ids = new Set(sortiert.map((a) => a.id));
  const haupt = aufgaben.filter((a) => !a.elternId).sort(nachReihenfolge);
  const ohne = haupt.filter((a) => !a.abschnittId || !ids.has(a.abschnittId));
  const gruppen: { abschnitt: Abschnitt | null; aufgaben: Aufgabe[] }[] = [];
  if (ohne.length > 0 || sortiert.length === 0) gruppen.push({ abschnitt: null, aufgaben: ohne });
  for (const s of sortiert) gruppen.push({ abschnitt: s, aufgaben: haupt.filter((a) => a.abschnittId === s.id) });
  return gruppen;
}

/** Unteraufgaben einer Aufgabe, sortiert. */
export const unteraufgaben = (aufgaben: Aufgabe[], id: string) => aufgaben.filter((a) => a.elternId === id).sort(nachReihenfolge);

/** Liste nach einer Änderung aktualisieren (ersetzen oder anhängen). */
export const eintragen = (liste: Aufgabe[], a: Aufgabe) => (liste.some((x) => x.id === a.id) ? liste.map((x) => (x.id === a.id ? a : x)) : [...liste, a]);

/** Entfernt eine Aufgabe samt aller Unteraufgaben (wie der Server beim Löschen). */
export function entfernen(liste: Aufgabe[], id: string): Aufgabe[] {
  const weg = new Set([id]);
  for (let neu = true; neu;) {
    neu = false;
    for (const a of liste) if (a.elternId && weg.has(a.elternId) && !weg.has(a.id)) { weg.add(a.id); neu = true; }
  }
  return liste.filter((a) => !weg.has(a.id));
}

/** Anzeige eines Feldwerts im Konflikthinweis. */
export function feldText(a: Partial<Aufgabe>, feld: string): string {
  if (feld === 'bezug') return a.bezugTyp ? `${a.bezugTyp} ${a.bezugId ?? ''}` : '–';
  if (feld === 'beteiligte') return a.beteiligte?.length ? a.beteiligte.join(', ') : '–';
  const v = (a as Record<string, unknown>)[feld];
  if (v === null || v === undefined || v === '') return '–';
  if (typeof v === 'boolean') return v ? 'ja' : 'nein';
  if (Array.isArray(v)) return v.length ? String(v.length) : '–';
  return String(v);
}

export const FELD_NAME: Record<string, string> = {
  titel: 'Titel', beschreibung: 'Beschreibung', zustaendig: 'Verantwortlich', beteiligte: 'Beteiligte', beginn: 'Beginn', faellig: 'Fällig', vorgaenger: 'Vorgänger', erledigt: 'Erledigt',
  prioritaet: 'Priorität', abschnittId: 'Abschnitt', reihenfolge: 'Reihenfolge', elternId: 'Übergeordnete Aufgabe', bezug: 'Bezug',
};

/**
 * Aufgaben zu einem Bezug (Block „Aufgaben“ im Detailbereich von Schritt, Prozess, Entscheidung): offene nach
 * Fälligkeit (ohne Datum zuletzt), dann Reihenfolge; erledigte zuletzt erledigte zuerst. „behalten“ = gerade
 * abgehakte, die noch bei den offenen stehen bleiben (durchgestrichen), damit die Zeile nicht wegspringt.
 */
export function zumBezug(aufgaben: Aufgabe[], behalten: ReadonlySet<string> = new Set()) {
  const offen = aufgaben.filter((a) => !a.erledigt || behalten.has(a.id))
    .sort((x, y) => (x.faellig ?? '9999').localeCompare(y.faellig ?? '9999') || nachReihenfolge(x, y));
  const erledigt = aufgaben.filter((a) => a.erledigt && !behalten.has(a.id))
    .sort((x, y) => (y.erledigtAm ?? '').localeCompare(x.erledigtAm ?? '') || nachReihenfolge(x, y));
  return { offen, erledigt };
}

// ---------- Abschnitte umsortieren ----------

/** Abschnitte in Anzeigereihenfolge. */
export const abschnitteSortiert = (abschnitte: Abschnitt[]) =>
  [...abschnitte].sort((a, b) => a.reihenfolge - b.reihenfolge || a.name.localeCompare(b.name));

/**
 * Neue Reihenfolge, damit Abschnitt <id> an Position <index> der übrigen Abschnitte steht
 * (0 = ganz vorn). null = bleibt, wo er ist.
 */
export function abschnittReihenfolge(abschnitte: Abschnitt[], id: string, index: number): number | null {
  const sortiert = abschnitteSortiert(abschnitte);
  const alt = sortiert.findIndex((s) => s.id === id);
  if (alt < 0) return null;
  const rest = sortiert.filter((s) => s.id !== id);
  const i = Math.max(0, Math.min(index, rest.length));
  if (i === alt) return null;
  return reihenfolgeZwischen(rest[i - 1]?.reihenfolge, rest[i]?.reihenfolge);
}

// ---------- Board: Spalten und Sortierung ----------

export type BoardSpalten = 'abschnitt' | 'zustaendig' | 'faellig' | 'kw' | 'datum';
export type BoardSortierung = 'manuell' | 'faellig' | 'zustaendig';
export const BOARD_SPALTEN: Record<BoardSpalten, string> = {
  abschnitt: 'Abschnitt', zustaendig: 'Verantwortlich', faellig: 'Fälligkeit (Zeitraum)', kw: 'Fälligkeit (Kalenderwoche)', datum: 'Fälligkeit (Datum)',
};
/** „manuell“ = eigene Reihenfolge wie in der Liste; Karten lassen sich nur in Spalten nach Abschnitt an eine Stelle ziehen. */
export const BOARD_SORTIERUNG: Record<BoardSortierung, string> = {
  manuell: 'Eigene Reihenfolge (ziehen)', faellig: 'Fälligkeit, dann Verantwortlich', zustaendig: 'Verantwortlich, dann Fälligkeit',
};

/** Die eigene Reihenfolge gibt es nur in Spalten nach Abschnitt; sonst wird nach Fälligkeit sortiert. */
export const wirksameSortierung = (spalten: BoardSpalten, sortierung: BoardSortierung): BoardSortierung =>
  (spalten !== 'abschnitt' && sortierung === 'manuell' ? 'faellig' : sortierung);

/** Kalenderwoche nach ISO 8601 (Montag bis Sonntag; die Woche gehört zum Jahr ihres Donnerstags) samt Montag und Sonntag. */
export function kalenderwoche(iso: string): { jahr: number; woche: number; montag: string; sonntag: string } {
  const t = new Date(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)));
  const wochentag = (t.getUTCDay() + 6) % 7; // Mo = 0
  const tag = (d: Date) => d.toISOString().slice(0, 10);
  const montag = new Date(t.getTime() - wochentag * 86_400_000);
  const donnerstag = new Date(montag.getTime() + 3 * 86_400_000);
  const jahr = donnerstag.getUTCFullYear();
  const woche = Math.floor((donnerstag.getTime() - Date.UTC(jahr, 0, 1)) / (7 * 86_400_000)) + 1;
  return { jahr, woche, montag: tag(montag), sonntag: tag(new Date(montag.getTime() + 6 * 86_400_000)) };
}

/** Einstellungen einer Aufgabenansicht, die in der Adresse stehen: Liste und Board ?spalten=&sortierung= (Gruppen bzw. Spalten), Gantt ?zoom=. */
export interface AnsichtOptionen { spalten?: BoardSpalten; sortierung?: BoardSortierung; zoom?: 'tag' | 'woche' | 'monat' }
const ZOOM_WERTE = ['tag', 'woche', 'monat'];

/** Optionen aus der Abfrage der Adresse; unbekannte Werte fallen weg. */
export function optionenAusSuche(suche: string): AnsichtOptionen {
  const q = new URLSearchParams(suche);
  const spalten = q.get('spalten'), sortierung = q.get('sortierung'), zoom = q.get('zoom');
  return {
    ...(spalten && spalten in BOARD_SPALTEN ? { spalten: spalten as BoardSpalten } : {}),
    ...(sortierung && sortierung in BOARD_SORTIERUNG ? { sortierung: sortierung as BoardSortierung } : {}),
    ...(zoom && ZOOM_WERTE.includes(zoom) ? { zoom: zoom as AnsichtOptionen['zoom'] } : {}),
  };
}

/** Abfrage für die Adresse: nur was zur Ansicht gehört (Liste und Board: Gruppen/Spalten und Sortierung, Gantt: Zoom). */
export function optionenSuche(o: AnsichtOptionen, ansicht: string): string {
  const q = new URLSearchParams();
  if (ansicht === 'board' || ansicht === 'liste') { if (o.spalten) q.set('spalten', o.spalten); if (o.sortierung) q.set('sortierung', o.sortierung); }
  if (ansicht === 'gantt' && o.zoom) q.set('zoom', o.zoom);
  return q.toString();
}

export type Zeitraum = 'ueberfaellig' | 'vergangen' | 'heute' | 'woche' | 'naechsteWoche' | 'spaeter' | 'ohne';
export const ZEITRAUM_TITEL: Record<Zeitraum, string> = {
  ueberfaellig: 'Überfällig', vergangen: 'Vergangen (erledigt)', heute: 'Heute', woche: 'Diese Woche',
  naechsteWoche: 'Nächste Woche', spaeter: 'Später', ohne: 'Ohne Datum',
};
// „Ohne Datum“ steht vorn: was noch keinen Termin hat, soll zuerst auffallen
const ZEITRAUM_FOLGE: Zeitraum[] = ['ohne', 'ueberfaellig', 'vergangen', 'heute', 'woche', 'naechsteWoche', 'spaeter'];

/** Zeitraum einer Aufgabe für das Board; Wochen laufen Montag bis Sonntag. */
export function zeitraum(a: Pick<Aufgabe, 'faellig' | 'erledigt'>, heute: string): Zeitraum {
  if (!a.faellig) return 'ohne';
  const d = tageZwischen(heute, a.faellig.slice(0, 10));
  if (d < 0) return a.erledigt ? 'vergangen' : 'ueberfaellig';
  if (d === 0) return 'heute';
  const wochentag = (new Date(Date.UTC(+heute.slice(0, 4), +heute.slice(5, 7) - 1, +heute.slice(8, 10))).getUTCDay() + 6) % 7; // Mo = 0
  const bisSonntag = 6 - wochentag;
  if (d <= bisSonntag) return 'woche';
  if (d <= bisSonntag + 7) return 'naechsteWoche';
  return 'spaeter';
}

const WOCHENTAG = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export interface BoardSpalte {
  key: string;
  titel: string;
  /** nur bei Spalten nach Abschnitt (null = „Ohne Abschnitt“) */
  abschnitt?: Abschnitt | null;
  /** nur bei Spalten nach Verantwortlich (null = ohne Verantwortliche(n)) */
  zustaendig?: string | null;
  aufgaben: Aufgabe[];
}

/**
 * Hauptaufgaben in Board-Spalten (nach Abschnitt, verantwortlicher Person, Zeitraum, Kalenderwoche oder Datum der Fälligkeit),
 * innerhalb sortiert: nach Fälligkeit und darin nach Person, nach Person und darin nach Fälligkeit, oder in eigener Reihenfolge.
 * Bei den Spalten nach Fälligkeit steht „Ohne Datum“ vorn, nach Verantwortlich „Niemand verantwortlich“. Die Liste gruppiert genauso.
 */
export function boardSpalten(aufgaben: Aufgabe[], abschnitte: Abschnitt[], spalten: BoardSpalten, sortierung: BoardSortierung,
  heute: string, personName: (z: string) => string): BoardSpalte[] {
  const name = (z: string | null) => (z ? personName(z) || z : '');
  const nachFaellig = (x: Aufgabe, y: Aufgabe) => (x.faellig ?? '9999').localeCompare(y.faellig ?? '9999');
  // Ohne verantwortliche Person zuerst: was noch niemandem gehört, soll auffallen (Thomas, 02.11.2026)
  const nachPerson = (x: Aufgabe, y: Aufgabe) => (x.zustaendig ? 1 : 0) - (y.zustaendig ? 1 : 0) || name(x.zustaendig).localeCompare(name(y.zustaendig), 'de');
  const art = wirksameSortierung(spalten, sortierung);
  const sortieren = (l: Aufgabe[]) => [...l].sort((x, y) =>
    art === 'faellig' ? nachFaellig(x, y) || nachPerson(x, y) || nachReihenfolge(x, y)
    : art === 'zustaendig' ? nachPerson(x, y) || nachFaellig(x, y) || nachReihenfolge(x, y)
    : nachReihenfolge(x, y));
  if (spalten === 'abschnitt')
    return nachAbschnitten(aufgaben, abschnitte).map((g) => ({ key: g.abschnitt?.id ?? '-', titel: g.abschnitt?.name ?? 'Ohne Abschnitt', abschnitt: g.abschnitt, aufgaben: sortieren(g.aufgaben) }));
  const haupt = aufgaben.filter((a) => !a.elternId);
  if (spalten === 'faellig')
    return ZEITRAUM_FOLGE.map((z) => ({ key: z, titel: ZEITRAUM_TITEL[z], aufgaben: sortieren(haupt.filter((a) => zeitraum(a, heute) === z)) }))
      .filter((s) => s.aufgaben.length > 0);
  if (spalten === 'kw' || spalten === 'datum') {
    const de = (iso: string, mitJahr = true) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.` + (mitJahr ? iso.slice(0, 4) : '');
    const gruppen = new Map<string, { titel: string; aufgaben: Aufgabe[] }>();
    for (const a of haupt.filter((x) => x.faellig).sort(nachFaellig)) {
      const tag = a.faellig!.slice(0, 10);
      const k = kalenderwoche(tag);
      const [key, titel] = spalten === 'kw'
        ? [`kw:${k.jahr}-${String(k.woche).padStart(2, '0')}`, `KW ${k.woche}${k.jahr === +heute.slice(0, 4) ? '' : '/' + k.jahr} · ${de(k.montag, false)}–${de(k.sonntag)}`]
        : ['d:' + tag, `${WOCHENTAG[(new Date(tag + 'T00:00:00Z').getUTCDay() + 6) % 7]} ${de(tag)} · KW ${k.woche}`];
      if (!gruppen.has(key)) gruppen.set(key, { titel, aufgaben: [] });
      gruppen.get(key)!.aufgaben.push(a);
    }
    const ohneDatum = haupt.filter((a) => !a.faellig);
    return [
      ...(ohneDatum.length ? [{ key: 'ohne', titel: ZEITRAUM_TITEL.ohne, aufgaben: sortieren(ohneDatum) }] : []),
      ...[...gruppen.entries()].map(([key, g]) => ({ key, titel: g.titel, aufgaben: sortieren(g.aufgaben) })),
    ];
  }
  const personen = new Map<string, string>();
  for (const a of haupt) if (a.zustaendig && !personen.has(a.zustaendig.toLowerCase())) personen.set(a.zustaendig.toLowerCase(), a.zustaendig);
  const liste: BoardSpalte[] = [...personen.entries()]
    .sort((x, y) => name(x[1]).localeCompare(name(y[1])))
    .map(([k, z]) => ({ key: 'p:' + k, titel: name(z), zustaendig: z, aufgaben: sortieren(haupt.filter((a) => a.zustaendig?.toLowerCase() === k)) }));
  const ohne = haupt.filter((a) => !a.zustaendig);
  if (ohne.length) liste.unshift({ key: 'p:-', titel: OHNE_VERANTWORTLICH, zustaendig: null, aufgaben: sortieren(ohne) });
  return liste;
}

// ---------- Infotext beim Überfahren mit der Maus ----------

export interface InfoZeile { name: string; wert: string; warn?: boolean }
export interface AufgabeInfoInhalt {
  titel: string;
  zeilen: InfoZeile[];
  /** Beschreibung (Markdown), auf etwa 300 Zeichen gekürzt; null = keine */
  beschreibung: string | null;
  gekuerzt: boolean;
}
/** Was zur Aufgabe bekannt ist – fehlt etwas (z. B. in „Meine Aufgaben“), entfällt die Zeile bzw. wird verkürzt angezeigt. */
export interface AufgabeInfoKontext {
  heute: string;
  personName?: (z: string) => string;
  /** alle Aufgaben des Projekts (Vorgänger-Titel, Unteraufgaben, übergeordnete Aufgabe) */
  alle?: Aufgabe[];
  abschnitte?: Abschnitt[];
  /** Name des Bezugs (Prozess, Schritt, Entscheidung) */
  bezugName?: string | null;
  /** Kunde und Projekt (nur „Meine Aufgaben“) */
  projekt?: string;
}

/** JJJJ-MM-TT → TT.MM.JJJJ */
export const datumLang = (iso: string) => { const [j, m, t] = iso.slice(0, 10).split('-'); return `${t}.${m}.${j}`; };

/** Markdown-Text auf höchstens `max` Zeichen kürzen (an einer Wortgrenze, mit „…“). */
export function textKuerzen(text: string, max = 300): { text: string; gekuerzt: boolean } {
  const t = text.trim();
  if (t.length <= max) return { text: t, gekuerzt: false };
  const schnitt = t.slice(0, max);
  const grenze = schnitt.search(/\s\S*$/);
  return { text: (grenze > max * 0.6 ? schnitt.slice(0, grenze) : schnitt).trimEnd() + ' …', gekuerzt: true };
}

/**
 * Inhalt des Infotexts zu einer Aufgabe (Liste, Board, Gantt, Meine Aufgaben): Titel, Abschnitt, Verantwortlich,
 * Beteiligte, Beginn/Fällig, Status (offen/erledigt, überfällig), Priorität, Vorgänger, Bezug, Unteraufgaben, Beschreibung gekürzt.
 * Leere Angaben fallen weg; Verantwortlich und Status stehen immer da.
 */
export function aufgabeInfo(a: Aufgabe, k: AufgabeInfoKontext): AufgabeInfoInhalt {
  const name = (z: string) => k.personName?.(z) || z;
  const z: InfoZeile[] = [];
  const dazu = (n: string, wert: string | null | undefined, warn?: boolean) => { if (wert) z.push(warn ? { name: n, wert, warn } : { name: n, wert }); };
  dazu('Projekt', k.projekt);
  const eltern = a.elternId ? k.alle?.find((x) => x.id === a.elternId) : null;
  dazu('Teil von', eltern?.titel);
  if (!a.elternId && k.abschnitte) dazu('Abschnitt', k.abschnitte.find((s) => s.id === a.abschnittId)?.name ?? 'Ohne Abschnitt');
  dazu('Verantwortlich', a.zustaendig ? name(a.zustaendig) : '–');
  dazu('Beteiligte', (a.beteiligte ?? []).map(name).join(', '));
  dazu('Beginn', a.beginn && datumLang(a.beginn));
  dazu('Fällig', a.faellig && datumLang(a.faellig));
  if (a.erledigt) dazu('Status', 'Erledigt' + (a.erledigtAm ? ' am ' + datumLang(a.erledigtAm.slice(0, 10)) : ''));
  else {
    const f = faelligkeit(a.faellig, k.heute);
    const tage = a.faellig ? -tageZwischen(k.heute, a.faellig.slice(0, 10)) : 0;
    dazu('Status', f === 'ueberfaellig' ? `Offen · überfällig seit ${tage === 1 ? 'gestern' : tage + ' Tagen'}` : f === 'heute' ? 'Offen · heute fällig' : 'Offen', f === 'ueberfaellig');
  }
  dazu('Priorität', a.prioritaet && PRIO_NAME[a.prioritaet]);
  const vor = a.vorgaenger ?? [];
  if (vor.length) dazu('Vorgänger', k.alle ? vor.map((id) => k.alle!.find((x) => x.id === id)?.titel ?? '(nicht sichtbar)').join(', ') : String(vor.length));
  if (a.bezugTyp) dazu('Bezug', BEZUG_NAME[a.bezugTyp] + (k.bezugName ? ': ' + k.bezugName : ''));
  if (k.alle) {
    const unter = unteraufgaben(k.alle, a.id);
    if (unter.length) dazu('Unteraufgaben', `${unter.filter((u) => u.erledigt).length}/${unter.length} erledigt`);
  }
  const b = a.beschreibung?.trim() ? textKuerzen(a.beschreibung) : null;
  return { titel: a.titel, zeilen: z, beschreibung: b?.text ?? null, gekuerzt: !!b?.gekuerzt };
}

// ---------- Eisenhower-Matrix ----------

export type Quadrant = 'sofort' | 'planen' | 'delegieren' | 'spaeter';
/** Wichtig = Priorität „Hoch“; dringend = überfällig, heute oder in den nächsten 7 Tagen fällig. */
export const QUADRANTEN: { key: Quadrant; titel: string; hinweis: string; wichtig: boolean; dringend: boolean }[] = [
  { key: 'sofort', titel: 'Sofort erledigen', hinweis: 'Wichtig und dringend', wichtig: true, dringend: true },
  { key: 'planen', titel: 'Einplanen', hinweis: 'Wichtig, nicht dringend', wichtig: true, dringend: false },
  { key: 'delegieren', titel: 'Abgeben', hinweis: 'Dringend, nicht wichtig', wichtig: false, dringend: true },
  { key: 'spaeter', titel: 'Später oder streichen', hinweis: 'Weder wichtig noch dringend', wichtig: false, dringend: false },
];

export const istWichtig = (a: Pick<Aufgabe, 'prioritaet'>) => a.prioritaet === 'hoch';
export const istDringend = (a: Pick<Aufgabe, 'faellig'>, heute: string) => ['ueberfaellig', 'heute', 'demnaechst'].includes(faelligkeit(a.faellig, heute));

export function quadrant(a: Pick<Aufgabe, 'prioritaet' | 'faellig'>, heute: string): Quadrant {
  const w = istWichtig(a), d = istDringend(a, heute);
  return w ? (d ? 'sofort' : 'planen') : (d ? 'delegieren' : 'spaeter');
}

/** Aufgaben je Quadrant, innerhalb nach Fälligkeit (ohne Datum zuletzt), dann Reihenfolge. */
export function matrix<T extends Pick<Aufgabe, 'prioritaet' | 'faellig' | 'reihenfolge' | 'titel'>>(liste: T[], heute: string) {
  return QUADRANTEN.map((q) => ({
    ...q,
    aufgaben: liste.filter((a) => quadrant(a, heute) === q.key)
      .sort((x, y) => (x.faellig ?? '9999').localeCompare(y.faellig ?? '9999') || x.reihenfolge - y.reihenfolge || x.titel.localeCompare(y.titel)),
  }));
}

/** Priorität nach dem Ziehen in einen Quadranten: in „wichtig“ → Hoch; aus „wichtig“ heraus → Mittel; sonst unverändert (undefined). */
export function prioritaetFuer(a: Pick<Aufgabe, 'prioritaet'>, wichtig: boolean): Prioritaet | undefined {
  if (wichtig) return istWichtig(a) ? undefined : 'hoch';
  return istWichtig(a) ? 'mittel' : undefined;
}

/**
 * Was sich beim Ziehen in einen Quadranten ändert (undefined: nichts). Wichtig ↔ nicht wichtig setzt die Priorität
 * (siehe prioritaetFuer). In „dringend“ gezogen wird die Aufgabe heute fällig (ein späterer Beginn rückt mit auf heute),
 * in „nicht dringend“ gezogen verliert sie ihr Fälligkeitsdatum.
 */
export function matrixAenderung(a: Pick<Aufgabe, 'prioritaet' | 'faellig' | 'beginn'>, ziel: { wichtig: boolean; dringend: boolean }, heute: string) {
  const f: { prioritaet?: Prioritaet; faellig?: string | null; beginn?: string } = {};
  const p = prioritaetFuer(a, ziel.wichtig);
  if (p) f.prioritaet = p;
  if (ziel.dringend !== istDringend(a, heute)) {
    f.faellig = ziel.dringend ? heute : null;
    if (ziel.dringend && a.beginn && a.beginn > heute) f.beginn = heute;
  }
  return Object.keys(f).length ? f : undefined;
}
